import { useMemo, type FC, useState } from 'react';
import type { HeroCoachProps } from './types';

import styled, { css } from 'styled-components';

import {
  ApiService,
  COLOR_YELLOW,
  baseMediaEndpoint,
  defaultWebValue,
  filterBackgroundColor,
  sanitizeRichText
} from 'shared/helpers/index';
import { useBook, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { Wrapper, Button, Mockup, Download } from '../Common';
import { HoverableDropzone } from '../HoverableDropzone';
import { Image as ImageComp } from 'shared/components/Common';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const Coaching = styled.div`
  position: relative;
  padding: 103px 0 123px;
  background: #fff;

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    padding: 83px 0;
`}

  ${({ theme: { $width } }) => `
    ${
      $width <= 768 &&
      `
      padding: 73px 0 63px;
    `
    }
  `}

  ${({ theme: { $width } }) => `
    ${
      $width <= 514 &&
      `
      padding: 193px 0 63px;
    `
    }
  `}
`;

const StyledWrapper = styled(Wrapper)`
  position: relative;
  z-index: 5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* gap: 40px; */

  ${({ theme: { $width } }) => `
    ${
      $width <= 991 &&
      `
      flex-direction: column-reverse;
      align-items: center;
    `
    }
  `}
`;

const TitleInner = styled.div`
  width: 580px;

  ${({ theme: { $width } }) => `
    ${
      $width <= 1254 &&
      `
      width: 460px;
    `
    }
    ${
      $width <= 991 &&
      `
      margin-top: 30px;
      width: 100%;
      text-align: center;
    `
    }
    
  `}
`;

const Title = styled.h2<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $largeHeadlineFont } }) => $largeHeadlineFont};
  margin-bottom: 35px;
  position: relative;
  width: 100%;
  flex-shrink: 0;

  ${({ theme: { $width } }) => `
    ${
      $width <= 991 &&
      `
      margin-bottom: 15px;
    `
    }
    ${
      $width <= 575 &&
      `
      margin-bottom: 20px;
    `
    }
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const SubtitleBot = styled.p<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme: { $subheadlineFont } }) => $subheadlineFont};
  line-height: 1.4em;
  margin-bottom: 110px;
  position: relative;

  ${({ theme: { $width } }) => `
    ${
      $width <= 1254 &&
      `
      margin-bottom: 80px;
    `
    }
    ${
      $width <= 991 &&
      `
      margin-bottom: 60px;
    `
    }
    ${
      $width <= 575 &&
      `
      margin-bottom: 40px;
    `
    }
  `}

  ${({ isEmpty, editable }) =>
    isEmpty
      ? `
    display: ${editable ? 'block' : 'none'};
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}
`;

const ButtonStyled = styled(Button)`
  display: inline-block;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 80%;
    padding-top: 24px;
    padding-bottom: 26px;
  `}
`;

const ImageInner = styled.div`
  display: flex;
  position: relative;
  width: 660px;
  height: 660px;
  border-radius: 50%;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 510px;
    height: 510px;
`}

  ${({ theme: { $width } }) =>
    $width <= 514 &&
    `
    width: 410px;
    height: 410px;
`}
`;

const MockUpStyled = styled((props) => <Mockup {...props} />)`
  width: 480px;
  height: auto;
  position: absolute;
  z-index: 6;
  top: -5%;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 380px;
`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 300px;
`}


  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 320px;
`}

  ${({ theme: { $width } }) =>
    $width <= 575 &&
    `
    width: 80%;
    `}

   ${({ mockup_id, theme: { $width } }) =>
    mockup_id === 3
      ? `width:400px; 

      ${
        $width <= 1254 &&
        `
        width: 309px;
        left: 50px;
      `
      }

      ${
        $width <= 991 &&
        `
        width: 247px;
        left: 50px;
      `
      }

      ${
        $width <= 768 &&
        `
        width: 263px;
        left: 0px;
      `
      }

      ${
        $width <= 575 &&
        `
        width: 70%;
        margin-bottom: 30px;
        left: 30px;
      `
      }
      ${
        $width <= 390 &&
        `
        left: 10px;
      `
      }
        `
      : mockup_id === 2
        ? `
      ${
        $width <= 1254 &&
        `
        width: 363px;
      `
      }

      ${
        $width <= 991 &&
        `
        width: 293px
      `
      }

      ${
        $width <= 768 &&
        `
        width: 313px
      `
      }

      ${
        $width <= 575 &&
        `
        left: -3%;
      `
      }
      ${
        $width <= 390 &&
        `
        width: 263px;
      `
      }
      ${
        $width <= 326 &&
        `
        width: 213px;
      `
      }
      `
        : mockup_id === 1
          ? `

      ${
        $width <= 326 &&
        `
        left: 0px;
      `
      }
      `
          : ``}
`;

const PhotoInner = styled.div<{ editable?: boolean }>`
  position: absolute;
  z-index: ${(props) => (props.editable ? 21 : 40)};
  right: 0%;
  bottom: 1%;
  width: 265px;
  height: 265px;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    width: 170px;
    height: 170px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    right: 0;
    width: 200px;
    height: 200px;
    `}
`;

const photoImages = css`
  width: 100% !important;
  height: 100% !important;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
    object-position: center center;
  }
`;

const PhotoImage = styled(ImageComp)<{ borderColor: string }>`
  border-radius: 50%;
  border: 10px solid;
  border-color: ${(props) => props.borderColor};
`;

const Arrow = styled.svg`
  position: absolute;
  z-index: 2;
  width: 210px;
  height: 50px;
  bottom: 1%;
  left: 21%;

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
  width: 145px;
  height: auto;
  bottom: 3%;
  left: 24%;
`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    width: 95px;
    height: auto;
    bottom: 3%;
    left: 50%;
`}
${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    display: none;
`}
`;

const OwnerText = styled.div`
  position: absolute;
  right: 50%;
  bottom: 45px;
  width: 180px;
  text-align: center;
  font-size: ${({ theme: { $tinyTextFont } }) => $tinyTextFont};
  line-height: 1.4em;
  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
  right: 38%;
  bottom: 18px;
`}

  ${({ theme: { $width } }) =>
    $width <= 991 &&
    `
    bottom: 9px;
    width: 172px;
`}

${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    right: 0;
    left: 0;
    margin: 0 auto;
    width: 100%;
    bottom: -9px;
`}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
  top: 0px;

  width: 467px;
  height: 627px;
  z-index: 5;

  ${({ theme: { $width } }) =>
    $width <= 1454 &&
    `

    width: 437px;
    height: 607px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    max-width: 437px;
    width: 357px;
    height: 467px !important;;
    left: 16px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: 357px;
    height: 467px !important;;
    left: 0px;
  `}

  ${({ theme: { $width } }) =>
    $width <= 514 &&
    `
    top: -45px;
    left: 4px;
  `}
`;

const BookMediaImg = styled(ImageComp)<{ isImageUploaded: boolean }>`
  min-width: 100%;
  width: initial !important;
  height: inherit !important;
  // object-fit: ${(props) => (props.isImageUploaded ? 'cover' : 'contain')};
  object-fit: cover;
  object-position: center center;
  border-radius: ${(props) => (props.isImageUploaded ? '5px' : '0px')};
`;

const WhiteBgCircle = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #fff;
  opacity: 0.72;
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  margin: 0 auto;
`;

export const V2: FC<HeroCoachProps> = (props) => {
  const {
    secondaryColor = COLOR_YELLOW,
    backgroundColor = COLOR_YELLOW,
    mockup,
    button = defaultWebValue,
    title = defaultWebValue,
    content = defaultWebValue,
    owner = defaultWebValue,
    textColor,
    hidden,
    meta = {},
    cover_thumbnail
  } = props;

  const {
    salesPageId,
    updateImageMeta,
    pagesThumbnails,
    purchaseElementId,
    isWebsiteOnly,
    removeMetaTag
  } = useSales();

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    editable,
    websiteAssembler
  } = useWebsiteEditor();

  const { pages, isCoverDisabled, bookMedia, bookId } = useBook();

  const { isEmpty: isTitleEmpty } = useIsEmpty(meta, 'title');
  const { isEmpty: isContentEmpty } = useIsEmpty(meta, 'content');
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  const [isLoading, setIsLoading] = useState([false, false]);
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const coverThumbnail = useMemo(() => {
    if (isCoverDisabled && (pages.length > 0 || pagesThumbnails.length > 0)) {
      return pagesThumbnails[0] || pages[0]?.image_preview;
    }
    return cover_thumbnail;
  }, [isCoverDisabled, pages, cover_thumbnail, pagesThumbnails]);

  const youHereImage = useMemo(() => {
    if (meta['you_here']) {
      if (baseMediaEndpoint?.includes('localhost') && !meta['you_here'].includes('localhost')) {
        return `${baseMediaEndpoint}${meta['you_here']}`;
      }
      return meta['you_here'];
    }
    return '/img/you_here.png';
  }, [meta]);

  const handleUpload = async (file: File, type: string, index: number) => {
    setIsLoading((prev) => prev.map((_, i) => (i === index ? true : false)));
    if (!meta['accessorKey'] || !editable) return;
    const formData = new FormData();
    if (isWebsiteOnly) formData.append('name', 'book_mockup');
    else formData.append('type', meta['accessorKey']);
    removeMetaTag(meta['accessorKey'], 'image_placeholder');
    formData.append('file', file);
    if (index === 0) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          setResize({ width, height });
          // updateImageMeta(meta['accessorKey'], width, 'width');
          // updateImageMeta(meta['accessorKey'], height, 'height');
        };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);
    }

    if (isWebsiteOnly) {
      formData.append('file', file);
      const response = await ApiService.post(
        `/book-generator/books/${bookId}/book_media/`,
        formData
      );
      updateImageMeta(meta['accessorKey'], response.data.file, 'image');
    } else {
      const response = await ApiService.post(`/sales/sales-page/${salesPageId}/media/`, formData);
      updateImageMeta(meta['accessorKey'], response.data.file, type);
    }

    setIsLoading([false, false]);
  };

  const bookMockup = useMemo(() => {
    if (meta['image'] && !meta['image_placeholder']) {
      setIsImageUploaded(true);
      return meta['image'];
    }

    const media = bookMedia?.find((media) => media.name === 'book_mockup');
    return media?.url;
  }, [bookMedia, meta]);

  return (
    <Coaching
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}>
      <StyledWrapper>
        <TitleInner>
          <Title
            id={title.id}
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, process.env['APP_TYPE'] === 'dashboard')
            }}
            style={{ color: filterBackgroundColor('#fff', textColor) }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="title"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isTitleEmpty}
          />
          <SubtitleBot
            id={content.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(content.val, editable) }}
            style={{ color: filterBackgroundColor('#fff', textColor) }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="content"
            data-placeholder="+"
            editable={process.env['APP_TYPE'] === 'dashboard'}
            isEmpty={isContentEmpty}
          />
          {meta['url'] ? (
            <Download textColor={textColor} button={button} meta={meta} />
          ) : (
            <ButtonStyled to={purchaseElementId} button={button} meta={meta} />
          )}
        </TitleInner>
        <ImageInner style={{ background: secondaryColor }}>
          {bookMockup ? (
            <HoverableDropzone
              isSalePage
              css={imageStyles as never}
              disabled={!editable}
              allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
              onUpload={(file) => handleUpload(file, 'image', 0)}
              loading={isLoading[0]}
              alreadyUploaded={Boolean(bookMockup)}
              imageFile={bookMockup}
              resizeSelector={editable}
              handleResize={({ width, height }) => {
                setResize({ width, height });
                updateImageMeta(meta['accessorKey'], width, 'width');
                updateImageMeta(meta['accessorKey'], height, 'height');
              }}
              initialPosition={{
                x: meta['x_1'] || 0,
                y: meta['y_1'] || 0,
                width: meta['d_width_1'],
                height: meta['d_height_1']
              }}
              meta={meta}
              positionEnabled
              handlePosition={(width: number, height: number, x: number, y: number) => {
                updateImageMeta(meta['accessorKey'], x, 'x_1');
                updateImageMeta(meta['accessorKey'], y, 'y_1');
                updateImageMeta(meta['accessorKey'], width, 'd_width_1');
                updateImageMeta(meta['accessorKey'], height, 'd_height_1');
              }}
              imageAspectRatio={{
                ratio: '3x4',
                pixels: '600x800'
              }}>
              <BookMediaImg
                isImageUploaded={isImageUploaded}
                src={bookMockup}
                width={resize.width || 500}
                height={resize.height || 500}
                alt="Book Mockup"
              />
            </HoverableDropzone>
          ) : (
            <MockUpStyled
              mockup={mockup?.file}
              mockup_id={mockup?.id}
              cover_thumbnail={coverThumbnail}
            />
          )}
          <PhotoInner>
            <HoverableDropzone
              isSalePage
              css={photoImages as never}
              disabled={!editable}
              allowedExtensions={['image/png', 'image/jpg', 'image/jpeg']}
              onUpload={(file) => handleUpload(file, 'you_here', 1)}
              loading={isLoading[1]}
              alreadyUploaded={Boolean(meta['you_here'])}
              imageFile={meta['you_here']}
              // imageAspectRatio={{
              //   ratio: '1x1',
              //   pixels: '1000x1000'
              // }}
              // resizeSelector={editable}
              // handleResize={({ width, height }) => {
              //   setResize({ width, height });
              //   updateImageMeta(meta['accessorKey'], width, 'width');
              //   updateImageMeta(meta['accessorKey'], height, 'height');
              // }}
              initialPosition={{
                x: meta['x_1'] || 0,
                y: meta['y_1'] || 0,
                width: meta['d_width_1'],
                height: meta['d_height_1']
              }}
              meta={meta}
              size="sm">
              <PhotoImage
                borderColor={backgroundColor}
                src={youHereImage}
                alt="author photo"
                width={500}
                height={500}
              />
            </HoverableDropzone>
          </PhotoInner>
          <Arrow viewBox="0 0 214 55" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.43604 3.03003C7.45862 7.74938 -6.03494 42.3986 10.4097 45.1644C22.9429 47.2723 35.9089 47.8807 48.7322 48.1012C85.1737 48.728 122.244 47.103 158.436 44.0256C160.191 43.8764 200.18 40.6223 199.062 37.9722C197.504 34.2841 190.683 29.0981 186.068 27.6034C180.927 25.938 180.254 26.2501 187.137 27.963C194.345 29.7568 202.092 31.8301 209.752 32.1585C216.278 32.4382 202.564 41.2728 201.529 41.9878C196.648 45.3574 191.466 50.0336 186.068 52.6563"
              stroke={textColor.value}
              strokeOpacity="0.75"
              strokeWidth="4.5"
              strokeLinecap="round"
            />
          </Arrow>
          <OwnerText
            id={owner.id}
            dangerouslySetInnerHTML={{ __html: sanitizeRichText(owner.val) }}
            style={{ color: filterBackgroundColor(secondaryColor, textColor) }}
            onClick={updateActiveElement}
            onBlur={resetActiveElement}
            data-accessor-parent={meta['accessorKey']}
            data-accessorkey="owner"
          />
          <WhiteBgCircle />
        </ImageInner>
      </StyledWrapper>
    </Coaching>
  );
};
